import { Component, OnInit, Input, Output, EventEmitter, KeyValueDiffers, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PagingSize } from '../paging/paging-size.model';
import { DrawTableModel } from '../paging/DrawTableModel';
import { PagingComponent } from '../paging/paging.component';
import { TableDefinition } from '../models/table-definition.model';
import { TableService } from '../table-service';
import {
  RowCheckedEventState,
  SelectedRows,
  SelectedRowsTableState,
  TableSort,
  TableSortDirection
} from '../models/table-sort.model';
import { IRecordIdentifier } from '@core/services/models/IVersionedRecord';
import { getPropertyValueByName } from '@shared/utils/helpers';
import { TableDefinitionColumn } from '../models/table-definition-column.model';

@UntilDestroy()
@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnInit {
  manageForm: FormGroup = this.initModelForm();
  @Input() tableDefinition: TableDefinition<any>;

  private _multiSelect: boolean = false;

  public get multiSelect(): boolean {
    return this._multiSelect;
  }

  @Input() set multiSelect(value: boolean) {
    this._multiSelect = value;
    this.resetSelectedRows();
  }

  @Input() multiSelectColumnHeaderText: string | null = null;

  @Input() singleSelect: boolean = false;
  @Input() deleteColumn: boolean = false;
  @Input() pagingFilter: boolean = false;
  @Input() deletedRows: IRecordIdentifier[];
  /*
   * if header underline is set to false the the first row has a background of #F8F8F8 and the second #FFFFFF
   * otherwise the sequence starts with #FFFFFF followed by #F8F8F8
   */
  @Input() headerUnderline: boolean = true;
  @Input() capsHeader: boolean = true;
  @Input() showPaging: boolean = true;

  /**
   * Show the paging controls at the bottom of the table
   */
  @Input() showPagingBottom: boolean = false;
  @Input() showPagingTop: boolean = false;
  @Input() rowFontSize: string = '16px';

  /* Use radio buttons instead of checkboxes for single selection */
  @Input() useRadioButtons: boolean = false;

  /* Disables the alternating row colors and give the whole table one background color */
  @Input() singleColor: boolean = false;

  /* Used when we only want to allow selection of a single record */
  @Input() disableSelection: boolean = false;

  _deletedRecords: IRecordIdentifier[] = [];

  get deletedRecords(): IRecordIdentifier[] {
    return this._deletedRecords;
  }

  @Input() set deletedRecords(val: IRecordIdentifier[]) {
    this._deletedRecords = val;
    this.tableDefinition.setDeletedRows(val);
  }

  /**
   * What to display when displaying the number of records in the table
   * eg 'Displaying 1 to 10 of 100 [records]'
   */
  @Input() filterRecordType: string = 'records';
  /**
   * Custom text to display instead of the default "Displaying 1-1 of 1 records"
   */
  @Input() customHeader: string;
  @Input() hidePagingHeader = false;

  @Output() deleteClicked: EventEmitter<number> = new EventEmitter<number>();
  @Output() undeleteClicked: EventEmitter<number> = new EventEmitter<number>();
  @Output() rowClicked: EventEmitter<SelectedRows> = new EventEmitter<SelectedRows>();
  @Output() itemsSelected: EventEmitter<FormArray<any>> = new EventEmitter<FormArray<any>>();
  @Output() showFilterChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() drawTable: EventEmitter<DrawTableModel> = new EventEmitter<DrawTableModel>();

  @ViewChild(PagingComponent) pagingComponent: PagingComponent;

  headerCheckboxSelected: boolean = false;
  headerCheckboxState: SelectedRowsTableState;

  currentSort: TableSort;
  currentPaging: PagingSize;
  differ: any;
  getPropertyValueByName = getPropertyValueByName;

  constructor(
    differs: KeyValueDiffers,
    private readonly tableService: TableService,
    private readonly _fb: FormBuilder
  ) {
    this.differ = differs.find({}).create();
  }

  ngOnInit() {
    this.headerCheckboxState = SelectedRowsTableState.NonSelected;
    this.tableService.onUnSelectAll.pipe(untilDestroyed(this)).subscribe(x => {
      this.resetSelectedRows();
      this.rowClicked.emit(new SelectedRows());
    });

    this.rowClicked.pipe(untilDestroyed(this)).subscribe(x => this.onCheckChange(x));
    this.draw();
  }

  initModelForm(): FormGroup {
    return this._fb.group({
      selectedItems: new FormArray([])
    });
  }

  onCheckChange(selectedRows: SelectedRows) {
    if (this.disableSelection) {
      return;
    }

    const formArray: FormArray<any> = this.manageForm.get('selectedItems') as FormArray<any>;

    formArray.clear();

    for (const row of selectedRows.rows) {
      formArray.push(
        new FormGroup({
          isDeleted: new FormControl(false),
          record: new FormControl(this.tableDefinition.getRowEntity(row.rowIndex))
        })
      );
    }

    this.itemsSelected.next(formArray);
  }

  getAttributeSort(sortColumnName: string | undefined): string | null {
    if (this.currentSort?.column?.sortColumnName === sortColumnName) {
      return this.currentSort?.direction === 'desc' ? 'descending' : 'ascending';
    }

    return null;
  }

  sort(column: TableDefinitionColumn): void {
    this.resetSelectedRows();
    //Default to always starting the sort descending.
    let direction = TableSortDirection.Descending;

    //if there was a default sort order defined, then use that
    if (this.tableDefinition.defaultSortDirection)
      direction =
        this.tableDefinition.defaultSortDirection == TableSortDirection.Ascending
          ? TableSortDirection.Descending
          : TableSortDirection.Ascending;

    // If the existing sort column is the same as the one we now want to sort by again
    // Then we are trying to reverse the sort order.
    if (this.currentSort?.column == column) {
      direction =
        this.currentSort.direction == TableSortDirection.Ascending
          ? TableSortDirection.Descending
          : TableSortDirection.Ascending;
    }

    this.currentSort = new TableSort();
    this.currentSort.column = column;
    this.currentSort.direction = direction;

    if (this.pagingComponent) {
      this.pagingComponent.page(1);
    }
  }

  page(page: PagingSize) {
    //unselect any rows
    this.resetSelectedRows();
    this.tableDefinition.currentPage = page.page;
    this.currentPaging = new PagingSize(page.page, page.pageSize);
    this.draw();
  }

  /**
   * Raise event to indicate that the user has changed the table configuration
   * and the table needs to be redrawn.
   * @returns
   */
  draw() {
    const model = new DrawTableModel();
    model.tableSort = this.currentSort;
    model.pagingSize = this.currentPaging;

    if (!model?.pagingSize && this.tableDefinition.dataURL) {
      // Initial call to draw the table with server side paging enabled, call this.page to
      // load the first page, this will cause this function (draw) to be called again, this time
      // with a value in  model.pagingSize, so this null conditions will be not be matched and
      // the execution will resume to emit the drawTable event that will be detected by PagedRecordsBase component
      const pagingSize = new PagingSize(1, this.tableDefinition.pageSize || 10);
      this.page(pagingSize);
      return;
    } else if (!model?.pagingSize) {
      return;
    }

    this.drawTable.next(model);
  }

  isRowDisabled(item: any): boolean {
    if (this.tableDefinition.isRowDisabled && typeof this.tableDefinition.isRowDisabled === 'function') {
      return this.tableDefinition.isRowDisabled(item) ?? false;
    }
    return false;
  }

  delete(rowIndex: number, rowId: any) {
    const checkedItem = this.tableDefinition.checkedColumns.find(t => t.rowId == rowId)!;

    //Mark record as deleted or not (allow undo)
    checkedItem.isDeleted = !checkedItem.isDeleted;

    //Unselect anything that we delete
    if (checkedItem.isDeleted) {
      checkedItem.isSelected = false;
      this.deleteClicked.emit(rowId);
    } else {
      this.undeleteClicked.emit(rowId);
    }
  }

  getRowId(index: number, row: any) {
    if (row == null || !this.tableDefinition) {
      return null;
    }

    return row[this.tableDefinition.detailIdField];
  }

  //this is used for dynamically setting column count css grid
  columnCss() {
    let cssValue = '';

    for (const column of this.tableDefinition.columns) {
      if (column.customWidthStyle) {
        cssValue = cssValue.concat(`${column.customWidthStyle} `);
      } else if (column.maxWidth) {
        cssValue = cssValue.concat(`${column.maxWidth}px `);
      } else if (column.gridMinMax) {
        cssValue = cssValue.concat(`minmax(${column.gridMinMax.min},${column.gridMinMax.max}) `);
      } else {
        cssValue = cssValue.concat('auto ');
      }
    }

    if (this.multiSelect) {
      cssValue = this.multiSelectColumnHeaderText ? cssValue.concat('min-content ') : cssValue.concat('50px ');
    }

    if (this.singleSelect) {
      cssValue = cssValue.concat('min-content ');
    }

    if (this.deleteColumn) {
      cssValue = cssValue.concat('50px ');
    }

    return cssValue;
  }

  /**
   * Calculate the col span for the deleted row place holder
   * @returns the value for the grid column style attribute. Equal to the number of columns of data in the table
   */
  deletedRowSpan() {
    return `span ${this.tableDefinition.columns.length}`;
  }

  /**
   * Register that a row has been clicked locally and emits the event to subscribers
   * @param rowIndex 0 based index of the selected row in the table
   * @param rowId the tableDefinition.detailIdField, typically the database table id
   */
  rowClick(column: TableDefinitionColumn, rowIndex: number, rowId: any) {
    if (column.selectRowOnClick) {
      if (this.tableDefinition.allowRowSelection) {
        if (this.singleSelect) {
          this.resetSelectedRows();
        }

        const rowsSelected = this.tableDefinition.checkedColumns.filter(item => item.isSelected).length;

        if (rowsSelected <= 1) {
          this.resetSelectedRows();
          this.tableDefinition.checkedColumns[rowIndex].isSelected = true;
          this.headerCheckboxState = SelectedRowsTableState.SomeSelected;
          this.rowClicked.emit(this.getSelectedRows(rowIndex));
        }
      }
    }
  }

  /**
   * Checkbox checked (as opposed to row click) event handler
   * @param event
   * @param rowId
   * @param rowIndex
   */
  registerCheckboxChecked(event: any, rowId: number, rowIndex: number) {
    if (this.singleSelect) {
      this.resetSelectedRows();
    }
    //even tho there is binding, we need to set this because it happens before the binding
    this.tableDefinition.checkedColumns[rowIndex].isSelected = this.useRadioButtons
      ? event.source.checked
      : event.target.checked;
    this.setCheckboxCheckStatus();
    this.rowClicked.emit(this.getSelectedRows(rowIndex));
  }

  /**
   * Event handler when the checkbox in the header is clicked
   */
  headerCheckboxCheck() {
    const anySelected = this.tableDefinition.checkedColumns.some(item => item.isSelected);

    for (const column of this.tableDefinition.checkedColumns) {
      column.isSelected = !anySelected;
    }

    this.setCheckboxCheckStatus();
    this.rowClicked.emit(this.getSelectedRows(0));
  }

  /**
   * Unselect all rows in the table
   */
  resetSelectedRows(): void {
    this.tableDefinition.unSelectAllRows();
    this.headerCheckboxState = SelectedRowsTableState.NonSelected;
  }

  /**
   * Set the status of the header check box based on the number of rows selected
   */
  private setCheckboxCheckStatus(): void {
    const { checkedColumns, data } = this.tableDefinition;

    const selected = checkedColumns.reduce((n, c) => n + (c.isSelected ? 1 : 0), 0);
    const total = data.length;

    if (selected === 0) {
      this.headerCheckboxState = SelectedRowsTableState.NonSelected;
      return;
    }

    if (selected === total) {
      this.headerCheckboxState = SelectedRowsTableState.AllSelected;
      return;
    }

    this.headerCheckboxState = SelectedRowsTableState.SomeSelected;
  }

  private getSelectedRows(clickedRowIndex: number): SelectedRows {
    const selectedRows = new SelectedRows();

    if (this.tableDefinition.data.length > 0) {
      for (const row of this.tableDefinition.checkedColumns.filter(row => row.isSelected)) {
        selectedRows.push(row.rowId, row.rowIndex);
      }
      if (this.tableDefinition.checkedColumns[clickedRowIndex].isSelected) {
        selectedRows.checkboxEventState = RowCheckedEventState.Checked;
      } else {
        selectedRows.checkboxEventState = RowCheckedEventState.Unchecked;
      }
    }

    return selectedRows;
  }

  filterChanged(showFilter: boolean) {
    this.showFilterChanged.next(showFilter);
  }
}
